import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ArticleCard } from '../components/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { Bookmark } from 'lucide-react';

import { API, authFetch } from '../lib/api';

export const BookmarksPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      login();
    }
  }, [authLoading, user, login]);

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    }
  }, [user]);

  const fetchBookmarks = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/bookmarks?limit=50`);
      
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    }
    setLoading(false);
  };

  const handleBookmarkChange = (articleId, isBookmarked) => {
    if (!isBookmarked) {
      // Remove from list if unbookmarked
      setArticles(prev => prev.filter(a => a.article_id !== articleId));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="bookmarks-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="mb-10 fade-up">
          <h1 
            className="text-3xl sm:text-4xl font-bold mb-2"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Your Bookmarks
          </h1>
          <p className="text-muted-foreground">
            Articles you've saved for later
          </p>
        </div>

        {/* Articles */}
        <div className="divide-y divide-border stagger-children">
          {loading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="py-6">
                <div className="flex gap-6">
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-3" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4 mb-3" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                  <Skeleton className="hidden sm:block w-32 h-32 rounded-lg" />
                </div>
              </div>
            ))
          ) : articles.length > 0 ? (
            articles.map(article => (
              <ArticleCard 
                key={article.article_id} 
                article={article}
                onBookmarkChange={handleBookmarkChange}
              />
            ))
          ) : (
            <div className="py-16 text-center">
              <Bookmark className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-2">
                No bookmarks yet
              </p>
              <p className="text-sm text-muted-foreground">
                Save articles to read them later
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BookmarksPage;
